import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    getPresentationScreenRegistry,
    updatePresentationScreenRegistry,
} from '../Api';
import type {
    PresentationScreenRegistryDto,
    PresentationScreenRegistryUpsertRequest,
} from '../models/dtos/PresentationScreenRegistryDto';

type PageMessage = { type: 'success' | 'error'; text: string } | null;

const DEFAULT_REGISTRY: PresentationScreenRegistryUpsertRequest = {
    version: 1,
    screens: [],
    groups: {
        home: ['home'],
        matches: [
            'matches_by_date',
            'matches_tabs',
            'leagues',
            'leagues_by_date',
            'sports_by_date',
            'top_matches',
            'top_matches_tab',
            'specific_championship',
        ],
        flash_bet: ['flash_bet', 'flash_bet_game', 'flash_bet_categories', 'flash_bet_match_start'],
        bets: ['bets', 'bets_list'],
        shop: ['shop_betcoins'],
    },
    ignoredClassNames: [
        'PresentationWebViewDialogFragment',
        'TourBetSelectionDialogFragment',
        'TourCompletedDialogFragment',
    ],
};

const formatRegistryJson = (registry: PresentationScreenRegistryUpsertRequest): string =>
    JSON.stringify(registry, null, 2);

type RawScreenMapping = {
    class?: string;
    className?: string;
    screenKey: string;
};

const parseRegistryJson = (raw: string): PresentationScreenRegistryUpsertRequest => {
    const parsed = JSON.parse(raw) as PresentationScreenRegistryDto & {
        screens?: RawScreenMapping[];
    };
    const screens = (parsed.screens ?? []).map((item: RawScreenMapping) => ({
        className: (item.className ?? item.class ?? '').trim(),
        screenKey: item.screenKey.trim().toLowerCase(),
    })).filter(item => item.className.length > 0 && item.screenKey.length > 0);

    const groups: Record<string, string[]> = {};
    Object.entries(parsed.groups ?? {}).forEach(([key, values]) => {
        const normalizedKey = key.trim().toLowerCase();
        const normalizedValues = (values ?? [])
            .map(value => value.trim().toLowerCase())
            .filter(Boolean);
        if (normalizedKey.length > 0 && normalizedValues.length > 0) {
            groups[normalizedKey] = Array.from(new Set(normalizedValues));
        }
    });

    const ignoredClassNames = (parsed.ignoredClassNames ?? [])
        .map(value => value.trim())
        .filter(Boolean);

    return {
        version: parsed.version && parsed.version > 0 ? parsed.version : 1,
        screens,
        groups,
        ignoredClassNames,
    };
};

export default function PresentationScreenRegistryPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [jsonText, setJsonText] = React.useState('');
    const [message, setMessage] = React.useState<PageMessage>(null);

    const loadRegistry = React.useCallback(async () => {
        setLoading(true);
        setMessage(null);
        const registry = await getPresentationScreenRegistry();
        if (registry == null) {
            setMessage({ type: 'error', text: 'Could not load screen registry.' });
            setJsonText(formatRegistryJson(DEFAULT_REGISTRY));
        } else {
            setJsonText(formatRegistryJson({
                version: registry.version,
                screens: registry.screens,
                groups: registry.groups,
                ignoredClassNames: registry.ignoredClassNames,
            }));
        }
        setLoading(false);
    }, []);

    React.useEffect(() => {
        void loadRegistry();
    }, [loadRegistry]);

    const importBundledTemplate = async () => {
        setMessage(null);
        try {
            const response = await fetch('/presentation-templates/screen-keys.json');
            if (!response.ok) {
                setMessage({ type: 'error', text: 'Bundled template file was not found.' });
                return;
            }
            const bundled = await response.json() as {
                screens?: RawScreenMapping[];
            };
            const screens = (bundled.screens ?? []).map(item => ({
                className: (item.className ?? item.class ?? '').trim(),
                screenKey: item.screenKey.trim().toLowerCase(),
            })).filter(item => item.className.length > 0 && item.screenKey.length > 0);

            const next: PresentationScreenRegistryUpsertRequest = {
                version: 1,
                screens,
                groups: DEFAULT_REGISTRY.groups,
                ignoredClassNames: DEFAULT_REGISTRY.ignoredClassNames,
            };
            setJsonText(formatRegistryJson(next));
            setMessage({
                type: 'success',
                text: `Imported ${screens.length} screens from bundled template. Save to publish to Android.`,
            });
        } catch {
            setMessage({ type: 'error', text: 'Failed to import bundled template.' });
        }
    };

    const saveRegistry = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const payload = parseRegistryJson(jsonText);
            const result = await updatePresentationScreenRegistry(payload);
            if (!result.ok) {
                setMessage({ type: 'error', text: result.error });
            } else {
                setJsonText(formatRegistryJson({
                    version: result.data.version,
                    screens: result.data.screens,
                    groups: result.data.groups,
                    ignoredClassNames: result.data.ignoredClassNames,
                }));
                setMessage({
                    type: 'success',
                    text: `Saved registry (${result.data.screens.length} screens, ${Object.keys(result.data.groups).length} groups).`,
                });
            }
        } catch {
            setMessage({ type: 'error', text: 'Invalid JSON. Check syntax and schema.' });
        }
        setSaving(false);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">Presentation screen registry</Typography>
                    <Button variant="outlined" onClick={() => navigate('/marketing/presentations')}>
                        Back to campaigns
                    </Button>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                    Android downloads this config and uses it for screen targeting. Keys are matched from fragment/dialog
                    class names. Groups let campaigns target multiple screens with one key (example: flash_bet).
                </Typography>

                {message && (
                    <Alert severity={message.type} onClose={() => setMessage(null)}>
                        {message.text}
                    </Alert>
                )}

                {loading ? (
                    <CircularProgress size={28} />
                ) : (
                    <>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={() => void loadRegistry()} disabled={saving}>
                                Reload
                            </Button>
                            <Button variant="outlined" onClick={() => void importBundledTemplate()} disabled={saving}>
                                Import bundled screens
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setJsonText(formatRegistryJson(DEFAULT_REGISTRY))}
                                disabled={saving}
                            >
                                Reset to defaults
                            </Button>
                            <Button variant="contained" onClick={() => void saveRegistry()} disabled={saving}>
                                {saving ? 'Saving…' : 'Save registry'}
                            </Button>
                        </Stack>

                        <TextField
                            label="Registry JSON"
                            value={jsonText}
                            onChange={e => setJsonText(e.target.value)}
                            multiline
                            minRows={24}
                            fullWidth
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                            helperText='Schema: { "version": 1, "screens": [{ "className": "HomeFragment", "screenKey": "home" }], "groups": { "flash_bet": ["flash_bet","flash_bet_game"] }, "ignoredClassNames": [] }'
                        />
                    </>
                )}
            </Stack>
        </Box>
    );
}
